<template>
  <div>
    <div class="book-detail-container p-p-4" v-if="book">
      <Card>
        <template #title>
          <div class="p-d-flex p-jc-between p-ai-center">
            <h2>{{ book.title }}</h2>
            <Button icon="pi pi-arrow-left" label="Back to Dashboard" class="p-button-secondary" @click="goBack" />
          </div>
        </template>
        <template #content>
          <div class="p-grid">
              <div class="p-col-12 p-md-8">
                  <h3>Details</h3>
                  <p><strong>Identifier:</strong> {{ book.identifier }}</p>
                  <p><strong>Description:</strong> {{ book.description }}</p>
                  <p><strong>Pages:</strong> {{ book.page_count }}</p>
                  <p><strong>Uploaded At:</strong> {{ new Date(book.uploaded_at).toLocaleString() }}</p>
              </div>
          </div>
        </template>
      </Card>

      <Card class="p-mt-4">
        <template #title>
          <h3>Pages</h3>
        </template>
        <template #content>
          <DataView :value="pages" :layout="'grid'" :paginator="true" :rows="pagination.limit" :totalRecords="book.page_count" @page="onPage" dataKey="id" :loading="pagesLoading" lazy>
            <template #grid="slotProps">
                <div class="grid-container">
                    <div v-for="(item, index) in slotProps.items" :key="index" class="grid-item">
                        <div 
                            class="page-thumbnail"
                            :class="{ 'selected': isSelected(item) }"
                        >
                            <div v-if="item.json_data" class="processed-indicator" title="This page has been processed"></div>
                            <div class="selection-icon" @click.stop="toggleSelection(item)">
                                <i class="pi" :class="isSelected(item) ? 'pi-check-circle' : 'pi-circle'"></i>
                            </div>
                            <Image :src="getPageUrl(item.thumbnail_path)" :alt="'Page ' + item.page_number" width="150" @click="showFullImage(item)" style="cursor: pointer;" />
                            <div class="page-number">{{ item.page_number }}</div>
                        </div> 
                    </div>
                </div>
            </template>
            <template #empty>
                <p>No pages found for this book, or the book is still being processed.</p>
            </template>
          </DataView>
        </template>
      </Card>

      <div class="selection-summary p-mt-4" v-if="selectedPages.length > 0">
        <Card>
          <template #title>
            Create Execution
          </template>
          <template #content>
            <p>{{ selectedPages.length }} page(s) selected.</p>
            <div class="p-d-flex p-flex-column p-flex-md-row p-ai-stretch p-md-ai-center p-gap-2">
                <div class="p-field p-flex-grow-1">
                    <label for="pageRange" class="p-sr-only">Selected pages/ranges</label>
                    <InputText id="pageRange" v-model="pageRangeInput" :readonly="true" placeholder="Selected pages/ranges" class="p-fluid space" />
                </div>
                <div class="p-d-flex p-gap-2 p-flex-wrap">
                    <Button label="Select All Visible" icon="pi pi-check-square" class="p-button-secondary space" @click="selectAllVisible" />
                    <Button label="Clear" icon="pi pi-times" class="p-button-danger space" @click="clearSelection" />
                    <Button label="Create Execution" class="space" icon="pi pi-plus" @click="createExecution" :loading="isCreatingExecution" />
                </div>
            </div>
          </template>
        </Card>
      </div>

      <Card class="p-mt-4">
        <template #title>
          Executions
        </template>
        <template #content>
          <DataTable :value="executions" responsiveLayout="scroll">
            <Column field="id" header="ID"></Column>
            <Column field="page_range" header="Page Range"></Column> <!-- Add this column -->
            <Column field="status" header="Status">
              <template #body="slotProps">
                <Tag :value="slotProps.data.status" :severity="getStatusSeverity(slotProps.data.status)" />
              </template>
            </Column>
            <Column field="created_at" header="Created At">
              <template #body="slotProps">
                {{ new Date(slotProps.data.created_at).toLocaleString() }}
              </template>
            </Column>
            <Column header="Actions">
                <template #body="slotProps">
                    <Button icon="pi pi-search" class="p-button-rounded p-button-info space" @click="viewExecution(slotProps.data)" />
                    <Button icon="pi pi-trash" class="p-button-rounded p-button-danger p-ml-2 space" @click="confirmDeleteExecution(slotProps.data)" />
                </template>
            </Column>
          </DataTable>
        </template>
      </Card>

    </div>
    <div v-else class="p-d-flex p-jc-center p-ai-center" style="height: 80vh;">
      <ProgressSpinner />
    </div>
    <Toast />
    <ConfirmDialog />

    <Dialog v-model:visible="isFullImageViewerVisible" :header="'Page ' + (selectedImageForPreview ? selectedImageForPreview.page_number : '')" :modal="true" :style="{ width: '90vw', height: '90vh' }">
        <div class="full-image-container">
            <img v-if="selectedImageForPreview" :src="getPageUrl(selectedImageForPreview.image_path)" alt="Full size page" class="full-size-image">
        </div>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, reactive, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import api from '@/services/api';
import Card from 'primevue/card';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import DataView from 'primevue/dataview';
//import DataViewLayoutOptions from 'primevue/dataviewlayoutoptions'   // optional
import Image from 'primevue/image';
import InputText from 'primevue/inputtext';
import Toast from 'primevue/toast';
import { useToast } from 'primevue/usetoast';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import Dialog from 'primevue/dialog';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from "primevue/useconfirm";

const route = useRoute();
const router = useRouter();
const toast = useToast();
const confirm = useConfirm();
const book = ref(null);
const pages = ref([]);
const pagesLoading = ref(false);
const selectedPages = ref([]);
const isCreatingExecution = ref(false);
const executions = ref([]);
const pagination = ref({
  page: 1,
  limit: 20,
});
const isFullImageViewerVisible = ref(false);
const selectedImageForPreview = ref(null);
let pollingInterval = null;

const bookId = route.params.id;

const pageRangeInput = computed(() => {
    if (selectedPages.value.length === 0) {
        return '';
    }
    const pageNumbers = selectedPages.value.map(p => p.page_number).sort((a, b) => a - b);
    
    const ranges = [];
    let start = pageNumbers[0];
    let end = pageNumbers[0];

    for (let i = 1; i < pageNumbers.length; i++) {
        if (pageNumbers[i] === end + 1) {
            end = pageNumbers[i];
        } else {
            ranges.push(start === end ? `${start}` : `${start}-${end}`);
            start = end = pageNumbers[i];
        }
    }
    ranges.push(start === end ? `${start}` : `${start}-${end}`);
    
    return ranges.join(', ');
});

const fetchBookDetails = async () => {
  try {
    const response = await api.get(`/books/${bookId}`);
    book.value = response.data;
    if (book.value.page_count > 0) {
      fetchBookPages();
    }
  } catch (error) {
    console.error('Error fetching book details:', error);
    router.push({ name: 'Dashboard' });
  }
};

const fetchExecutions = async () => {
  try {
    const response = await api.get(`/books/${bookId}/executions`);
    const newExecutions = response.data;

    if (executions.value.length > 0) {
      newExecutions.forEach(newExec => {
        const oldExec = executions.value.find(old => old.id === newExec.id);
        if (oldExec && oldExec.status !== newExec.status) {
          toast.add({ 
            severity: 'info', 
            summary: 'Execution Status Updated', 
            detail: `Execution for page range ${newExec.page_range || newExec.id} is now ${newExec.status}`,
            life: 5000 
          });
        }
      });
    }

    executions.value = newExecutions;

    const isStillProcessing = newExecutions.some(exec => !['COMPLETED', 'FAILED'].includes(exec.status.toUpperCase()));
    if (!isStillProcessing) {
      stopPolling();
    }

  } catch (error) {
    console.error("Failed to fetch executions:", error);
    stopPolling();
  }
};

const startPolling = () => {
    stopPolling();
    const isProcessing = executions.value.some(exec => !['COMPLETED', 'FAILED'].includes(exec.status.toUpperCase()));
    if (isProcessing) {
        pollingInterval = setInterval(fetchExecutions, 5000);
    }
};

const stopPolling = () => {
    if (pollingInterval) {
        clearInterval(pollingInterval);
        pollingInterval = null;
    }
};

const fetchBookPages = async () => {
  pagesLoading.value = true;
  try {
    const response = await api.get(`/books/${bookId}/pages`, {
      params: { skip: pagination.skip, limit: pagination.limit },
    });
    if (!Array.isArray(response.data)) {
      throw new Error('Expected an array of pages');
    }
    if (response.data.length === 0) {
      toast.add({ severity: 'warn', summary: 'No Pages Found', detail: 'This book has no pages or is still being processed.', life: 3000 });
    }
    pages.value = response.data;
  } catch (error) {
    console.error('Error fetching book pages:', error);
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to load pages.', life: 3000 });
  } finally {
    pagesLoading.value = false;
  }
};

const onPage = (event) => {
  pagination.value.page = event.page + 1; // PrimeVue page is 0-indexed
  loadPages();
};

const loadPages = async () => {
  pagesLoading.value = true;
  try {
    const response = await api.get(`/books/${bookId}/pages`, {
      params: { skip: (pagination.value.page - 1) * pagination.value.limit, limit: pagination.value.limit },
    });
    if (!Array.isArray(response.data)) {
      throw new Error('Expected an array of pages');
    }
    if (response.data.length === 0 && pagination.value.page > 1) {
      pagination.value.page--;
      toast.add({ severity: 'warn', summary: 'No More Pages', detail: 'No more pages found. You have been moved to the last available page.', life: 3000 });
      return;
    }
    pages.value = response.data;
  } catch (error) {
    console.error('Error fetching book pages:', error);
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to load pages.', life: 3000 });
  } finally {
    pagesLoading.value = false;
  }
};

const getPageUrl = (path) => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.accestex.it';
  return `${baseUrl}/${path}`;
};

const isSelected = (page) => {
  return selectedPages.value.some(p => p.id === page.id);
};

const toggleSelection = (page) => {
  if (isSelected(page)) {
    selectedPages.value = selectedPages.value.filter(p => p.id !== page.id);
  } else {
    selectedPages.value.push(page);
  }
};

const selectAllVisible = () => {
    pages.value.forEach(page => {
        if (!isSelected(page)) {
            selectedPages.value.push(page);
        }
    });
};

const clearSelection = () => {
    selectedPages.value = [];
};

const createExecution = async () => {
    isCreatingExecution.value = true;
    
    const selected_pages = selectedPages.value.map(p => p.page_number).sort((a, b) => a - b);

    if (selected_pages.length === 0) {
        toast.add({ severity: 'warn', summary: 'Warning', detail: 'No pages selected. Please select pages to create an execution.', life: 3000 });
        isCreatingExecution.value = false;
        return;
    }

    try {
        await api.post(`/books/${bookId}/executions`, { selected_pages });
        toast.add({ severity: 'success', summary: 'Success', detail: 'Execution created successfully!', life: 3000 });
        clearSelection();
        await fetchExecutions(); // Refresh the executions list
        startPolling();
    } catch (error) {
        console.error('Error creating execution:', error);
        toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to create execution.', life: 3000 });
    } finally {
        isCreatingExecution.value = false;
    }
};

const goBack = () => {
  router.push({ name: 'DashboardHome' });
};

onMounted(async () => {
  await fetchBookDetails();
  await fetchExecutions();
  startPolling();
});

onUnmounted(() => {
  stopPolling();
});

const getStatusSeverity = (status) => {
  switch (status) {
    case 'COMPLETED':
      return 'success';
    case 'PENDING':
      return 'warning';
    case 'PROCESSING':
      return 'info';
    case 'FAILED':
      return 'danger';
    default:
      return 'info';
  }
};

const viewExecution = (execution) => {
  router.push({ name: 'ExecutionDetail', params: { bookId: book.value.id, executionId: execution.id } });
};

const confirmDeleteExecution = (execution) => {
    confirm.require({
        message: 'Are you sure you want to delete this execution?',
        header: 'Confirmation',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
            deleteExecution(execution.id);
        }
    });
};

const deleteExecution = async (executionId) => {
    try {
        await api.delete(`/executions/${executionId}`);
        toast.add({ severity: 'success', summary: 'Success', detail: 'Execution deleted successfully', life: 3000 });
        fetchExecutions(); // Refresh the list
    } catch (error) {
        console.error("Failed to delete execution:", error);
        toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete execution', life: 3000 });
    }
};

const showFullImage = (item) => {
  selectedImageForPreview.value = item;
  isFullImageViewerVisible.value = true;
};

</script>

<style scoped>
.space {
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
}
.book-detail-container {
  max-width: 1200px;
  margin: 0 auto;
}

.grid-container {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem; /* Spazio tra gli elementi */
}

.grid-item {
    flex: 1 1 150px; /* Flex-grow, flex-shrink, flex-basis */
    max-width: 180px; /* Per contenere l'immagine e il padding */
    display: flex;
    justify-content: center;
}

.page-thumbnail {
  position: relative;
  text-align: center;
  border: none;
  border-radius: 4px;
  padding: 1.40rem;
  transition: box-shadow 0.3s, border-color 0.3s;
  cursor: pointer;
  display: inline-block; /* Si adatta al contenuto */
  overflow: hidden; /* Nasconde le parti del triangolo che escono */
}

.processed-indicator {
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 15px 15px 0 0;
  border-color: #28a745 transparent transparent transparent;
  z-index: 2;
}

.page-thumbnail:hover {
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.page-thumbnail.selected {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px var(--primary-color);
}

.selection-icon {
  position: absolute;
  top: 5px;
  right: 5px;
  color: var(--primary-color);
  font-size: 1.2rem;
  cursor: pointer;
}

.page-number {
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: #666;
}

.selection-summary {
  position: sticky;
  bottom: 0;
  z-index: 100;
  background-color: rgba(255, 255, 255, 0.95);
  box-shadow: 0 -2px 10px rgba(0,0,0,0.1);
  padding: 1rem;
}

.full-image-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.full-size-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

::v-deep(.p-image-preview-indicator) {
    cursor: pointer;
}

::v-deep(.p-paginator) {
    justify-content: center;
}
</style>
